import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";

const SEVERITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#3b82f6" };
const STATUS_OPTIONS = ["all", "open", "actioned", "resolved"];

function StoreIssues() {
    const { siteId } = useParams();
    const { hasPermission } = useAuth();
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("open");
    const [severityFilter, setSeverityFilter] = useState("");

    const loadIssues = () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (severityFilter) params.severity = severityFilter;
        api.storeIssues(siteId, params)
            .then((data) => { setIssues(data); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(loadIssues, [siteId, statusFilter, severityFilter]);

    const handleAction = async (issueId, action) => {
        try {
            if (action === "action") await api.actionIssue(issueId, {});
            else if (action === "verify") await api.verifyIssue(issueId, {});
            loadIssues();
        } catch (err) {
            console.error("Action failed:", err.message);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                    <Link to="/stores" style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>Stores</Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <Link to={`/store/${siteId}`} style={{ color: "var(--text-muted)", textDecoration: "none", fontSize: 13 }}>{siteId}</Link>
                    <span style={{ color: "var(--text-muted)" }}>/</span>
                    <span style={{ fontSize: 13 }}>Issues</span>
                </div>
                <h2>Issues {"\u2014"} Site {siteId}</h2>
                <p>{issues.length} issues found</p>
            </div>

            <div style={{
                background: "linear-gradient(135deg, #1e3a5f, #1a2744)",
                border: "1px solid #3b82f6",
                borderRadius: 8,
                padding: "14px 20px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
            }}>
                <div style={{ color: "#e8eaf0", fontSize: 14 }}>
                    <span style={{ fontWeight: 600 }}>Not sure what to do?</span>
                    <span style={{ color: "#94a3b8", marginLeft: 8 }}>See our step-by-step guide on how to action issues.</span>
                </div>
                <a href="/guide.html" target="_blank" rel="noopener noreferrer" style={{
                    background: "#3b82f6",
                    color: "#fff",
                    padding: "8px 18px",
                    borderRadius: 6,
                    textDecoration: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                }}>View Guide</a>
            </div>

            <div className="filter-row" style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                </select>
                <select className="filter-select" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                    <option value="">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                </select>
            </div>

            {loading && <LoadingSpinner message="Loading issues..." />}

            {!loading && issues.length === 0 && (
                <div className="card" style={{ padding: 40, textAlign: "center" }}>
                    <h3 style={{ color: "var(--text-secondary)" }}>No issues found</h3>
                    <p style={{ color: "var(--text-muted)" }}>Try changing your filters.</p>
                </div>
            )}

            {!loading && issues.map((issue) => {
                const ageDays = issue.first_detected_at ? Math.floor((Date.now() - new Date(issue.first_detected_at).getTime()) / (1000 * 60 * 60 * 24)) : null;
                const ageText = ageDays === null ? null : ageDays === 0 ? "Today" : ageDays === 1 ? "1 day" : `${ageDays} days`;
                const ageCol = ageDays === null ? "var(--text-muted)" : ageDays > 7 ? "#ef4444" : ageDays > 3 ? "#f97316" : "var(--text-muted)";
                return (
                <div key={issue.id} className="card" style={{ marginBottom: 12, padding: 16 }}>
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                        {issue.imageUrl && (
                            <img
                                src={issue.imageUrl}
                                alt={issue.title}
                                style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6, flexShrink: 0 }}
                            />
                        )}
                        <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                <span style={{
                                    color: SEVERITY_COLORS[issue.severity] || "var(--text-muted)",
                                    fontWeight: 700, fontSize: 11, textTransform: "uppercase",
                                    padding: "2px 8px", borderRadius: 4,
                                    background: `${SEVERITY_COLORS[issue.severity] || "#5c6078"}20`,
                                }}>
                                    {issue.severity}
                                </span>
                                <span style={{
                                    fontSize: 11, color: "var(--text-muted)", padding: "2px 8px",
                                    borderRadius: 4, background: "var(--bg-hover)", textTransform: "uppercase",
                                }}>
                                    {issue.status}
                                </span>
                                {issue.category && (
                                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{issue.category}</span>
                                )}
                                {issue.zone && (
                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{issue.zone}</span>
                                )}
                                {ageText && issue.status !== 'resolved' && (
                                    <span style={{ fontSize: 11, fontWeight: 600, color: ageCol, marginLeft: "auto" }}>
                                        {ageText} old
                                    </span>
                                )}
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{issue.title}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 16 }}>
                                <span>Occurrences: {issue.occurrence_count}</span>
                                <span>First: {new Date(issue.first_detected_at).toLocaleDateString()}</span>
                                <span>Last: {new Date(issue.last_detected_at).toLocaleDateString()}</span>
                            </div>
                            {(issue.status === "open" || issue.status === "reopened") && hasPermission('issues.resolve') && (
                                <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                                    <button
                                        onClick={() => handleAction(issue.id, "action")}
                                        style={{
                                            fontSize: 11, padding: "3px 8px", borderRadius: 4,
                                            border: "1px solid var(--border)", background: "var(--bg-hover)",
                                            color: "var(--text-secondary)", cursor: "pointer",
                                        }}
                                    >
                                        Actioned
                                    </button>
                                </div>
                            )}
                            {issue.status === "actioned" && hasPermission('issues.verify') && (
                                <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                                    <button
                                        onClick={() => handleAction(issue.id, "verify")}
                                        style={{
                                            fontSize: 11, padding: "3px 8px", borderRadius: 4,
                                            border: "1px solid #22c55e", background: "rgba(34,197,94,0.1)",
                                            color: "#22c55e", cursor: "pointer",
                                        }}
                                    >
                                        Verify
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
            })}
        </div>
    );
}

export default StoreIssues;
